import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import { useUser } from "@/hooks/useUser";
import { CORPORATE_ROLES, canAccessPage, hasPermission, getCommissionRate } from "@/shared/companyRoles";

const CompanyContext = createContext();

export const useCompany = () => {
    const context = useContext(CompanyContext);
    if (!context) {
        throw new Error('useCompany must be used within a CompanyProvider');
    }
    return context;
};

export const CompanyProvider = ({ children }) => {
    const [selectedCompanyId, setSelectedCompanyIdState] = useState(() => {
        const saved = localStorage.getItem('crm_selected_company_id');
        if (!saved || saved === 'null' || saved === 'undefined') return 'personal';
        return saved;
    });

    const setSelectedCompanyId = (id) => {
        setSelectedCompanyIdState(id);
        localStorage.setItem('crm_selected_company_id', id);
    };

    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'crm_selected_company_id') {
                setSelectedCompanyIdState(e.newValue || 'personal');
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    const { user } = useUser();
    const userEmail = user?.primaryEmailAddress?.emailAddress || '';
    const isAdmin = !!user?.publicMetadata?.is_platform_admin || userEmail.toLowerCase() === 'contact.core829@gmail.com';

    const workGroups = useQuery(api.work_groups.getUserWorkgroups) || [];
    const rawUserCompanies = useQuery(api.company_roles.getUserCompanies);
    const userCompanies = rawUserCompanies || [];

    const [userCompaniesLoaded, setUserCompaniesLoaded] = useState(false);
    useEffect(() => {
        if (rawUserCompanies !== undefined) {
            setUserCompaniesLoaded(true);
        }
    }, [rawUserCompanies]);

    // Auto-select first company if in "personal" mode and user has companies
    // Use a ref to prevent re-renders from causing infinite loops
    const [autoSelected, setAutoSelected] = useState(false);
    
    useEffect(() => {
        if (selectedCompanyId === 'personal' && userCompanies.length > 0 && !autoSelected) {
            const companyId = userCompanies[0].company_account_id || userCompanies[0]._id;
            if (companyId) {
                setSelectedCompanyId(companyId);
                setAutoSelected(true);
            }
        }
    }, [selectedCompanyId, userCompanies, autoSelected]);

    let activeCompanyId = selectedCompanyId === 'personal' ? null : selectedCompanyId;
    let activeWorkGroupId = null;

    if (selectedCompanyId?.startsWith('wg_')) {
        activeWorkGroupId = selectedCompanyId.replace('wg_', '');
        const wg = workGroups.find(w => w._id === activeWorkGroupId);
        activeCompanyId = wg ? wg.company_account_id : null;
    }

    // If userCompanies loaded but no company selected yet, use the first one directly
    if (!activeCompanyId && userCompanies.length > 0) {
        activeCompanyId = userCompanies[0].company_account_id || userCompanies[0]._id;
    }

    // Fetch user's role in the active company — stable args via useMemo
    const roleQueryArgs = useMemo(() =>
        activeCompanyId ? { company_account_id: activeCompanyId } : "skip",
        [activeCompanyId]
    );
    const userRole = useQuery(api.company_roles.getUserRole, roleQueryArgs);

    // Role-based permissions
    const roleConfig = useMemo(() => {
        if (isAdmin) {
            return {
                rank: 6,
                roleType: 'founder',
                label: 'Founder & Superadmin',
                department: 'executive',
                maxCommission: 0.30,
                permissions: ['*'],
                canAccess: ['*'],
                canAssignRoles: ['*'],
            };
        }
        if (!userRole?.role_type) return null;
        return CORPORATE_ROLES[userRole.role_type] || null;
    }, [userRole, isAdmin]);

    const userRank = roleConfig?.rank || 0;
    const userRoleType = userRole?.role_type || '';
    const userDepartment = roleConfig?.department || '';
    const userMaxCommission = roleConfig?.maxCommission || 0;

    // Helper functions exposed via context
    const canUserAccessPage = (pageName) => {
        if (isAdmin) return true;
        if (!roleConfig) return false;
        return canAccessPage(userRoleType, pageName);
    };

    const userHasPermission = (permission) => {
        if (isAdmin) return true;
        if (!roleConfig) return false;
        return hasPermission(userRoleType, permission);
    };

    const canUserAssignRole = (roleType) => {
        if (isAdmin) return true;
        if (!roleConfig) return false;
        if (roleConfig.canAssignRoles.includes('*')) return true;
        return roleConfig.canAssignRoles.includes(roleType);
    };

    const isCompanyMember = !!userRole || isAdmin || (userCompanies && userCompanies.length > 0);
    // userRole is undefined when the query is skipped (no active company).
    // A skipped query never resolves — don't treat it as loading.
    const roleQuerySkipped = !activeCompanyId;
    const isLoading = (!roleQuerySkipped && userRole === undefined) || (selectedCompanyId === 'personal' && userCompanies.length > 0 && !autoSelected) || !userCompaniesLoaded;

    return (
        <CompanyContext.Provider value={{
            selectedCompanyId,
            setSelectedCompanyId,
            activeCompanyId,
            activeWorkGroupId,
            userRole,
            roleConfig,
            userRank,
            userRoleType,
            userDepartment,
            userMaxCommission,
            isAdmin,
            isCompanyMember,
            canUserAccessPage,
            userHasPermission,
            canUserAssignRole,
            isLoading,
            userCompanies,
            userCompaniesLoaded,
        }}>
            {children}
        </CompanyContext.Provider>
    );
};
